import React from 'react'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { AgedDebtors } from '../../../../back-end/common/billyDashboard'
import DashboardCard from './dashboardCard'
import { Loading } from '../ui/Loading/Loading'
import { Col, Row } from 'react-bootstrap'
import { dollarFormat } from '../../utilities/format'
import { DateTime } from 'luxon'
import { TestingStaff } from '../../screens/dashboard/dashboardWrapper'

interface AgedDebtorsProps {
	firmView: boolean
	testingStaff?: TestingStaff
}

const AgedDebtorsCard = (props: AgedDebtorsProps) => {
	const { appState } = React.useContext(AppContext)

	const [agedDebtors, setAgedDebtors] = React.useState<AgedDebtors | null>(null)
	const [errorMsg, setErrorMsg] = React.useState<string | null>(null)

	React.useEffect(() => {
		const getData = async () => {
			const testingStaffXpmID = props.testingStaff ? '/' + props.testingStaff.xpmID : ''
			const agedDebtorsRes = await Request.get(`billydashboard/ageddebtors${testingStaffXpmID}?firm=${props.firmView}`)

			setAgedDebtors(agedDebtorsRes.data.agedDebtors)

			if (!agedDebtorsRes.data.success) {
				setErrorMsg(agedDebtorsRes.data.message)
			}
		}

		setAgedDebtors(null)
		setErrorMsg(null)
		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState, props])

	if (errorMsg) {
		return (
			<DashboardCard background="salmon">
				<div className="center-flex p-3">
					<span className="span-body-2 span-white">{errorMsg}</span>
				</div>
			</DashboardCard>
		)
	}

	if (!agedDebtors) {
		return (
			<DashboardCard background="salmon">
				<div className="center-flex">
					<Loading show style={{ color: 'white' }} />
				</div>
			</DashboardCard>
		)
	}

	const colWidth = {
		group: 2,
		overMax: 1,
		balance: 2,
		lastReceipt: 2,
		months: {
			total: 5,
			this: 6,
			last: 6,
		},
	}

	return (
		<DashboardCard background="salmon">
			<Row>
				<Col className="d-flex flex-column">
					<span className="span-body-2 span-bold span-white">+{agedDebtors.maxInvoiceDaysOutstanding} DAY AGED DEBTORS</span>

					<span className="font-800-50-58 span-white">{agedDebtors.clientGroupsAgedDebtors.length}</span>
				</Col>
				<Col className="d-flex flex-column justify-content-end align-items-end">
					<span className="font-800-50-58 span-white">
						{dollarFormat(agedDebtors.clientGroupsAgedDebtors.reduce((acc, ad) => (acc += ad.debtOverMaxOutstanding), 0))}
					</span>
				</Col>
			</Row>
			{agedDebtors.clientGroupsAgedDebtors.length > 0 && (
				<Row className="pt-4">
					<Col sm={colWidth.group}>
						<span className="span-body-2 span-bold span-white">GROUP</span>
					</Col>
					<Col sm={colWidth.overMax} className="d-flex justify-content-end">
						<span className="span-body-2 span-bold span-white">+{agedDebtors.maxInvoiceDaysOutstanding}</span>
					</Col>
					<Col sm={colWidth.balance} className="d-flex justify-content-end">
						<span className="span-body-2 span-bold span-white">BALANCE</span>
					</Col>
					<Col sm={colWidth.lastReceipt} className="d-flex justify-content-end">
						<span className="span-body-2 span-bold span-white">LAST RECEIPT</span>
					</Col>
					<Col sm={colWidth.months.total}>
						<Row>
							<Col sm={colWidth.months.this} className="d-flex justify-content-end">
								<span className="span-body-2 span-bold span-white">{agedDebtors.thisMonthName.toUpperCase()} RECEIPTS</span>
							</Col>
							<Col sm={colWidth.months.last} className="d-flex justify-content-end">
								<span className="span-body-2 span-bold span-white">{agedDebtors.lastMonthName.toUpperCase()} RECEIPTS</span>
							</Col>
						</Row>
					</Col>
				</Row>
			)}
			{agedDebtors.clientGroupsAgedDebtors.map((ad) => (
				<Row className="pt-2" key={ad.clientGroupXpmID}>
					<Col sm={colWidth.group}>
						<span className="span-body-2 span-white">{ad.clientGroupName}</span>
					</Col>
					<Col sm={colWidth.overMax} className="d-flex justify-content-end">
						<span className="span-body-2 span-white">{dollarFormat(ad.debtOverMaxOutstanding)}</span>
					</Col>
					<Col sm={colWidth.balance} className="d-flex justify-content-end">
						<span className="span-body-2 span-white">{dollarFormat(ad.debtBalance)}</span>
					</Col>
					<Col sm={colWidth.lastReceipt} className="d-flex justify-content-end">
						<span className="span-body-2 span-white">{ad.lastPaymentDate ? DateTime.fromISO(ad.lastPaymentDate).toFormat('d/M/yy') : '-'}</span>
					</Col>
					<Col sm={colWidth.months.total}>
						<Row>
							<Col sm={colWidth.months.this} className="d-flex justify-content-end">
								<span className="span-body-2 span-white">{dollarFormat(ad.thisMonthTotalPayments)}</span>
							</Col>
							<Col sm={colWidth.months.last} className="d-flex justify-content-end">
								<span className="span-body-2 span-white">{dollarFormat(ad.lastMonthTotalPayments)}</span>
							</Col>
						</Row>
					</Col>
				</Row>
			))}
		</DashboardCard>
	)
}

export default AgedDebtorsCard
